import React, { createContext, useContext, useMemo, useState } from 'react';

type ThemeMode = 'light' | 'dark' | 'system';
export type Preferences = {
  theme: ThemeMode;
  language: string;
  timezone: string;
  branding?: {
    productName?: string;
    primaryColor?: string;
    secondaryColor?: string;
    logoDataUrl?: string;
    faviconDataUrl?: string;
  };
};

type PreferencesContextValue = {
  preferences: Preferences;
  setPreferences: (prefs: Preferences, options?: { persist?: boolean }) => void;
  formatDateTime: (value: string | number | Date, options?: Intl.DateTimeFormatOptions) => string;
};

const defaultPrefs: Preferences = {
  theme: 'system',
  language: 'en',
  timezone: 'UTC',
};

const PreferencesContext = createContext<PreferencesContextValue>({
  preferences: defaultPrefs,
  setPreferences: () => {},
  formatDateTime: (value: string | number | Date, options?: Intl.DateTimeFormatOptions) =>
    new Intl.DateTimeFormat('en', { timeZone: 'UTC', ...(options || {}) }).format(new Date(value)),
});

function readLocal(): Preferences {
  if (typeof localStorage === 'undefined') return defaultPrefs;

  // Try to read branding from localStorage
  let branding: Preferences['branding'] = undefined;
  try {
    const brandingStr = localStorage.getItem('melp-branding');
    if (brandingStr) {
      branding = JSON.parse(brandingStr);
      console.log('[PreferencesContext] Loaded branding from localStorage:', branding);
    } else {
      console.log('[PreferencesContext] No branding found in localStorage');
    }
  } catch (e) {
    console.error('[PreferencesContext] Failed to parse branding from localStorage:', e);
  }

  return {
    theme: (localStorage.getItem('melp-theme-preference') as ThemeMode) || 'system',
    language: localStorage.getItem('melp-language') || 'en',
    timezone: localStorage.getItem('melp-timezone') || 'UTC',
    branding,
  };
}

export function PreferencesProvider({ children }: { children: React.ReactNode }) {
  const [preferences, setPreferencesState] = useState<Preferences>(() => readLocal());

  const setPreferences = (prefs: Preferences, options?: { persist?: boolean }) => {
    console.log('[PreferencesContext] Setting preferences:', prefs);

    // WARNING: Check if branding is missing (potential bug)
    if (!prefs.branding) {
      console.warn('[PreferencesContext] ⚠️ WARNING: Branding is missing in preferences update!');
      console.trace('[PreferencesContext] Call stack:');
    }

    setPreferencesState(prefs);
    if (options?.persist !== false && typeof localStorage !== 'undefined') {
      localStorage.setItem('melp-theme-preference', prefs.theme);
      localStorage.setItem('melp-language', prefs.language);
      localStorage.setItem('melp-timezone', prefs.timezone);

      // Persist branding to localStorage
      if (prefs.branding) {
        localStorage.setItem('melp-branding', JSON.stringify(prefs.branding));
        console.log('[PreferencesContext] Persisted branding to localStorage');
      } else {
        localStorage.removeItem('melp-branding');
        console.log('[PreferencesContext] Removed branding from localStorage (no branding provided)');
      }
    }
    if (typeof document !== 'undefined') {
      document.documentElement.lang = prefs.language || 'en';
    }
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('melp-theme-updated'));
    }
  };

  const formatDateTime = useMemo(() => {
    return (value: string | number | Date, options?: Intl.DateTimeFormatOptions) => {
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return 'Unknown';
      try {
        return new Intl.DateTimeFormat(preferences.language || 'en', {
          timeZone: preferences.timezone || 'UTC',
          ...(options || {}),
        }).format(date);
      } catch {
        return date.toLocaleString();
      }
    };
  }, [preferences.language, preferences.timezone]);

  const ctx: PreferencesContextValue = {
    preferences,
    setPreferences,
    formatDateTime,
  };

  return <PreferencesContext.Provider value={ctx}>{children}</PreferencesContext.Provider>;
}

export function usePreferences() {
  return useContext(PreferencesContext);
}
